import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Select, Alert, Space, Typography, Button, Spin } from 'antd';
import {
  VideoCameraOutlined,
  SettingOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SmileOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { api } from '../../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

const PageContainer = styled.div`
  padding: 0;
  background: #000;
  min-height: 100vh;
  position: relative;
`;

const FullscreenContainer = styled.div`
  width: 100%;
  height: ${props => props.$isFullscreen ? '100vh' : 'calc(100vh - 100px)'};
  background: #000;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ControlBar = styled.div`
  background: rgba(0, 0, 0, 0.9);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 2px solid rgba(180, 160, 247, 0.3);
  z-index: 1000;

  ${props => props.$isFullscreen && `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    opacity: 0;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  `}
`;

const VideoContainer = styled.div`
  flex: 1;
  width: 100%;
  position: relative;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoCanvas = styled.canvas`
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;  /* TV-OPTIMIZED: contain is faster than cover (no cropping math) */
  display: block;
  background: #000;
  image-rendering: crisp-edges;
  image-rendering: -webkit-optimize-contrast;
`;

const StatusOverlay = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.8);
  padding: 8px 16px;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 999;
`;

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$connected ? '#52c41a' : '#ff4d4f'};
  animation: ${props => props.$connected ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
  color: white;
  z-index: 100;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
`;

const FullscreenButton = styled(Button)`
  background: rgba(180, 160, 247, 0.2) !important;
  border-color: rgba(180, 160, 247, 0.5) !important;
  color: white !important;

  &:hover {
    background: rgba(180, 160, 247, 0.4) !important;
    border-color: rgba(180, 160, 247, 0.8) !important;
  }
`;

const SmileChallenge = () => {
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);  // Cache canvas context to prevent flickering
  const wsRef = useRef(null);
  const containerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isManualDisconnectRef = useRef(false);  // Prevent reconnection on manual disconnect
  const isRenderingRef = useRef(false);  // TV-specific frame skipping to prevent lag

  // WebSocket connection function (defined early to avoid hoisting issues)
  const connectWebSocket = useCallback(() => {
    try {
      setLoading(true);
      setError(null);

      // CRITICAL: Close existing WebSocket before creating new one (prevents multiple connections)
      if (wsRef.current) {
        wsRef.current.onclose = null;  // Prevent reconnection loop
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
        wsRef.current = null;
      }

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Build WebSocket URL - stream Camera 1 GUI display with pre-rendered smile overlays
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const wsProtocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
      const wsHost = apiUrl.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}//${wsHost}/api/smile-challenge/ws-gui/1`;

      console.log('Connecting to Smile Challenge WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.binaryType = 'arraybuffer';

      ws.onopen = () => {
        console.log('Smile Challenge WebSocket connected');
        setConnected(true);
        setLoading(false);
        setError(null);
        reconnectAttemptsRef.current = 0;
        isManualDisconnectRef.current = false;  // Reset manual disconnect flag on successful connection
      };

      ws.onmessage = (event) => {
        if (typeof event.data === 'string') {
          // JSON message (likely error)
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'error') {
              setError(data.message);
              setLoading(false);
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        } else {
          // Binary data (JPEG frame) - Using ImageBitmap for fastest rendering

          isRenderingRef.current = true;

          createImageBitmap(new Blob([event.data], { type: 'image/jpeg' }))
            .then(bitmap => {
              const canvas = canvasRef.current;
              if (canvas) {
                // Initialize canvas context once (cached to prevent flickering)
                if (!ctxRef.current) {
                  ctxRef.current = canvas.getContext('2d', {
                    alpha: false,              // No transparency = faster rendering
                    desynchronized: true,      // Allow async rendering = smoother
                    willReadFrequently: false  // Optimized for write-only (video streaming)
                  });
                  ctxRef.current.imageSmoothingEnabled = false;  // Crisp rendering, no blur
                }

                const ctx = ctxRef.current;

                // Only resize canvas when dimensions change (not every frame)
                if (canvas.width !== bitmap.width) canvas.width = bitmap.width;
                if (canvas.height !== bitmap.height) canvas.height = bitmap.height;

                // Draw using cached context (eliminates flickering)
                ctx.drawImage(bitmap, 0, 0);
                bitmap.close(); // Free memory immediately
              }
              isRenderingRef.current = false;  // Ready for next frame
            })
            .catch(err => {
              console.error('Error decoding frame:', err);
              isRenderingRef.current = false;  // Reset on error
            });
        }
      };

      ws.onerror = (event) => {
        console.error('Smile Challenge WebSocket error:', event);
        setError('Connection error. Please check your network.');
        setLoading(false);
      };

      ws.onclose = () => {
        console.log('Smile Challenge WebSocket closed');
        setConnected(false);

        // Don't reconnect if manually disconnected (e.g., component unmount, user navigation)
        if (isManualDisconnectRef.current) {
          console.log('Manual disconnect - not reconnecting');
          isManualDisconnectRef.current = false;  // Reset flag
          return;
        }

        // Attempt reconnection with exponential backoff (only for network errors)
        if (reconnectAttemptsRef.current < 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          console.log(`Reconnecting in ${delay}ms... (attempt ${reconnectAttemptsRef.current + 1}/5)`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            connectWebSocket();
          }, delay);
        } else {
          setError('Connection lost. Please refresh the page.');
        }
      };

    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
      setError('Failed to establish connection.');
      setLoading(false);
    }
  }, [setLoading, setError, setConnected]);  // Include state setters

  const disconnectWebSocket = useCallback(() => {
    // Mark as manual disconnect to prevent auto-reconnection
    isManualDisconnectRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.onclose = null;  // Prevent reconnection loop
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnected(false);
  }, [setConnected]);  // Include state setter

  // Load cameras on mount
  useEffect(() => {
    loadCameras();
  }, []);

  // Connect WebSocket when camera selected
  useEffect(() => {
    if (selectedCamera) {
      connectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [selectedCamera, connectWebSocket, disconnectWebSocket]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const loadCameras = async () => {
    try {
      setLoading(true);
      const response = await api.getCameras();
      setCameras(response.cameras || []);

      // Always use camera 1 for Smile Challenge
      const camera1 = response.cameras?.find(cam => cam.camera_id === "1");
      if (camera1) {
        setSelectedCamera("1");
      } else {
        setError('Camera 1 not found. Please check camera configuration.');
      }

      if (!response.main_py_running) {
        setError('No active camera feeds detected. Please ensure main.py is running.');
      } else if (!camera1) {
        setError('Camera 1 is not available.');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Failed to load cameras:', err);
      setError('Failed to load camera list. Please check API connection.');
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!isFullscreen) {
        // Enter fullscreen
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else if (containerRef.current.webkitRequestFullscreen) {
          await containerRef.current.webkitRequestFullscreen();
        } else if (containerRef.current.mozRequestFullScreen) {
          await containerRef.current.mozRequestFullScreen();
        } else if (containerRef.current.msRequestFullscreen) {
          await containerRef.current.msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const cameraName = cameras.find(cam => cam.camera_id === selectedCamera)?.zone ||
                     `Camera ${selectedCamera}`;

  return (
    <PageContainer>
      <FullscreenContainer ref={containerRef} $isFullscreen={isFullscreen}>
        <VideoContainer>
          <VideoCanvas ref={canvasRef} />

          {/* Fullscreen button overlay (always visible) */}
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1001
          }}>
            <FullscreenButton
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              size="large"
            >
              {isFullscreen ? 'Exit' : 'Fullscreen'}
            </FullscreenButton>
          </div>

          <StatusOverlay $isFullscreen={isFullscreen}>
            <StatusDot $connected={connected} />
            {connected ? 'LIVE' : 'OFFLINE'}
          </StatusOverlay>

          {loading && (
            <LoadingOverlay>
              <Spin size="large" />
              <div style={{ fontSize: 18 }}>Connecting to Smile Challenge...</div>
            </LoadingOverlay>
          )}

          {error && !loading && (
            <ErrorOverlay>
              <Alert
                message="Connection Error"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
              />
            </ErrorOverlay>
          )}
        </VideoContainer>
      </FullscreenContainer>
    </PageContainer>
  );
};

export default SmileChallenge;
